import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';
import {
  CheckCircle, Clock, AlertCircle, Users, BookOpen, Calendar,
  UserX, DoorOpen, AlertTriangle, Filter
} from 'lucide-react';

interface Period {
  id: number;
  name: string;
  code: string;
  startDate: string;
  endDate: string;
  programName: string;
}

interface ExecutedData {
  id: number;
  classDate: string;
  startTime: string;
  endTime: string;
  professorName: string;
  classroomName: string;
  status: string;
  attendanceCount: number;
  totalStudents: number;
}

interface KardexEntry {
  id: number;
  courseId: number;
  courseName: string;
  courseCode: string;
  classDate: string;
  startTime: string;
  endTime: string;
  professorName: string;
  classroomName: string;
  executed: boolean;
  executedData: ExecutedData | null;
  status: string;
  attendanceCount: number;
  totalStudents: number;
  sessionNumber: number | null;
  totalSessions: number | null;
  isDiscrepant: boolean;
  discrepancyDays: number;
}

interface CourseSummary {
  courseId: number;
  courseName: string;
  courseCode: string;
  planned: number;
  executed: number;
  discrepancies: number;
}

interface KardexData {
  period: {
    id: number;
    name: string;
    code: string;
    startDate: string;
    endDate: string;
  };
  summary: {
    totalPlanned: number;
    totalExecuted: number;
    pendingClasses: number;
    totalDiscrepancies: number;
    completionRate: string;
  };
  coursesSummary: CourseSummary[];
  kardex: KardexEntry[];
}

interface UnassignedItem {
  id: number;
  courseId: number;
  courseName: string;
  courseCode: string;
  classDate: string;
  startTime: string;
  endTime: string;
  professorName: string | null;
  classroomName: string | null;
  missingProfessor: boolean;
  missingClassroom: boolean;
  sessionNumber: number | null;
  totalSessions: number | null;
}

interface UnassignedData {
  summary: {
    noProfessor: number;
    noClassroom: number;
    noBoth: number;
    total: number;
  };
  items: UnassignedItem[];
}

type StatusFilter = 'planeada' | 'ejecutada' | 'pendiente';
type UnassignedFilter = 'all' | 'noProfessor' | 'noClassroom' | 'noBoth';
type ActiveTab = 'kardex' | 'unassigned';

const formatDateToDDMMYYYY = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'Sin fecha';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function KardexClases() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<ActiveTab>('kardex');
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  const [kardexData, setKardexData] = useState<KardexData | null>(null);
  const [unassignedData, setUnassignedData] = useState<UnassignedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilter>>(new Set(['planeada', 'ejecutada', 'pendiente']));
  const [onlyDiscrepant, setOnlyDiscrepant] = useState(false);
  const [unassignedFilter, setUnassignedFilter] = useState<UnassignedFilter>('all');
  const [unassignedCourseId, setUnassignedCourseId] = useState<number | null>(null);

  const [periodSearchTerm, setPeriodSearchTerm] = useState('');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);

  const toggleStatusFilter = (status: StatusFilter) => {
    setStatusFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  useEffect(() => {
    loadPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriodId) {
      loadKardex();
      loadUnassigned();
      setSelectedCourseId(null);
      setUnassignedCourseId(null);
    }
  }, [selectedPeriodId]);

  useEffect(() => {
    const handleSchedulesChanged = () => {
      loadPeriods();
      if (selectedPeriodId) {
        loadKardex();
        loadUnassigned();
      }
    };
    window.addEventListener('schedulesChanged', handleSchedulesChanged);
    return () => window.removeEventListener('schedulesChanged', handleSchedulesChanged);
  }, [selectedPeriodId]);

  const loadPeriods = async () => {
    try {
      setLoadingPeriods(true);
      const response = await fetch('/api/attendance/periods', { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar períodos');
      const data = await response.json();
      setPeriods(data);
    } catch (error) {
      console.error('Error loading periods:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudieron cargar los períodos' });
    } finally {
      setLoadingPeriods(false);
    }
  };

  const loadKardex = async () => {
    if (!selectedPeriodId) return;
    try {
      setLoading(true);
      const response = await fetch(`/api/attendance/kardex?periodId=${selectedPeriodId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar kardex');
      const data = await response.json();
      setKardexData(data);
    } catch (error) {
      console.error('Error loading kardex:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cargar el kardex' });
    } finally {
      setLoading(false);
    }
  };

  const loadUnassigned = async () => {
    if (!selectedPeriodId) return;
    try {
      setLoadingUnassigned(true);
      const response = await fetch(`/api/attendance/unassigned?periodId=${selectedPeriodId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Error al cargar asignaciones');
      const data = await response.json();
      setUnassignedData(data);
    } catch (error) {
      console.error('Error loading unassigned:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cargar las materias sin asignación' });
    } finally {
      setLoadingUnassigned(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const filteredUnassigned = (unassignedData?.items || []).filter(item => {
    const matchesCourse = unassignedCourseId ? item.courseId === unassignedCourseId : true;
    const matchesFilter =
      unassignedFilter === 'all' ? true :
      unassignedFilter === 'noProfessor' ? (item.missingProfessor && !item.missingClassroom) :
      unassignedFilter === 'noClassroom' ? (item.missingClassroom && !item.missingProfessor) :
      unassignedFilter === 'noBoth' ? (item.missingProfessor && item.missingClassroom) :
      true;
    return matchesCourse && matchesFilter;
  });

  const unassignedCourses = Array.from(
    new Map((unassignedData?.items || []).map(i => [i.courseId, { id: i.courseId, name: i.courseName, code: i.courseCode }])).values()
  );

  const selectedPeriodData = periods.find(p => p.id === selectedPeriodId);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kardex de Clases</h1>
          <p className="text-gray-600">Comparativo de clases planeadas vs ejecutadas</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Período / Grupo Académico
          </label>
          <div className="relative max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder={loadingPeriods ? "Cargando períodos..." : "Buscar por clave o nombre..."}
                value={isPeriodDropdownOpen ? periodSearchTerm : (selectedPeriodData ? `${selectedPeriodData.code} - ${selectedPeriodData.name}` : '')}
                onChange={(e) => {
                  setPeriodSearchTerm(e.target.value);
                  if (!isPeriodDropdownOpen) setIsPeriodDropdownOpen(true);
                }}
                onFocus={() => {
                  setIsPeriodDropdownOpen(true);
                  if (selectedPeriodId) setPeriodSearchTerm('');
                }}
                onBlur={() => setTimeout(() => setIsPeriodDropdownOpen(false), 200)}
                disabled={loadingPeriods}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
            </div>

            {isPeriodDropdownOpen && !loadingPeriods && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {periods.filter(period => {
                  const s = periodSearchTerm.toLowerCase();
                  return period.name.toLowerCase().includes(s) || period.code.toLowerCase().includes(s) || (period.programName && period.programName.toLowerCase().includes(s));
                }).length > 0 ? (
                  periods.filter(period => {
                    const s = periodSearchTerm.toLowerCase();
                    return period.name.toLowerCase().includes(s) || period.code.toLowerCase().includes(s) || (period.programName && period.programName.toLowerCase().includes(s));
                  }).map(period => (
                    <div
                      key={period.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedPeriodId === period.id ? 'bg-blue-100' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSelectedPeriodId(period.id);
                        setPeriodSearchTerm('');
                        setIsPeriodDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">{period.code} - {period.name}</div>
                      <div className="text-xs text-gray-500">
                        {period.programName}
                        {period.startDate && ` | ${formatDateToDDMMYYYY(period.startDate)} - ${formatDateToDDMMYYYY(period.endDate)}`}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">No se encontraron períodos con "{periodSearchTerm}"</div>
                )}
              </div>
            )}

            {selectedPeriodId && !isPeriodDropdownOpen && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPeriodId(null);
                  setPeriodSearchTerm('');
                  setKardexData(null);
                  setUnassignedData(null);
                }}
                className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                title="Limpiar selección"
              >✕</button>
            )}
          </div>
        </div>

        {selectedPeriodId && (
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-1">
              <button
                onClick={() => setActiveTab('kardex')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === 'kardex'
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Kardex de Clases
                </span>
              </button>
              <button
                onClick={() => setActiveTab('unassigned')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === 'unassigned'
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Sin Asignación
                  {unassignedData && unassignedData.summary.total > 0 && (
                    <span className="bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {unassignedData.summary.total}
                    </span>
                  )}
                </span>
              </button>
            </nav>
          </div>
        )}

        {/* ─── TAB: KARDEX ─── */}
        {activeTab === 'kardex' && (
          <>
            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Cargando kardex...</p>
              </div>
            )}

            {kardexData && !loading && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-600">Clases Planeadas</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{kardexData.summary.totalPlanned}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-600">Clases Ejecutadas</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{kardexData.summary.totalExecuted}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm text-gray-600">Pendientes</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600">{kardexData.summary.pendingClasses}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <span className="text-sm text-gray-600">⚠️ Desfasadas</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{kardexData.summary.totalDiscrepancies}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Avance</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{kardexData.summary.completionRate}%</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Resumen por Materia</h3>
                    {selectedCourseId && (
                      <Button variant="outline" size="sm" onClick={() => setSelectedCourseId(null)}>
                        Mostrar todas
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto pb-2">
                    <div className="flex gap-4" style={{ minWidth: 'min-content' }}>
                      {kardexData.coursesSummary.map((course) => (
                        <div
                          key={course.courseId}
                          className={`border rounded-lg p-4 min-w-[280px] flex-shrink-0 cursor-pointer transition-all hover:shadow-md ${
                            selectedCourseId === course.courseId
                              ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50'
                              : 'hover:border-gray-400'
                          }`}
                          onClick={() => setSelectedCourseId(selectedCourseId === course.courseId ? null : course.courseId)}
                        >
                          <h4 className="font-medium text-gray-900">{course.courseCode} - {course.courseName}</h4>
                          <div className="mt-2 flex items-center gap-4 flex-wrap">
                            <span className="text-sm text-gray-500">Ejecutadas: <span className="font-semibold text-green-600">{course.executed}</span></span>
                            <span className="text-sm text-gray-500">de <span className="font-semibold">{course.planned}</span> planeadas</span>
                            {course.discrepancies > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700">
                                <AlertTriangle className="w-3 h-3" /> {course.discrepancies} desfasada{course.discrepancies > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${course.planned > 0 ? (course.executed / course.planned) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <h3 className="text-lg font-semibold">
                      Detalle de Clases
                      {selectedCourseId && kardexData.coursesSummary.find(c => c.courseId === selectedCourseId) && (
                        <span className="ml-2 text-sm font-normal text-blue-600">
                          - Filtrando: {kardexData.coursesSummary.find(c => c.courseId === selectedCourseId)?.courseName}
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-sm text-gray-600 font-medium">Filtrar por estado:</span>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={statusFilters.has('planeada')} onChange={() => toggleStatusFilter('planeada')} className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Calendar className="w-3 h-3" /> Planeada
                        </span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={statusFilters.has('ejecutada')} onChange={() => toggleStatusFilter('ejecutada')} className="w-4 h-4 text-green-600 border-gray-300 rounded" />
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3" /> Ejecutada
                        </span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={statusFilters.has('pendiente')} onChange={() => toggleStatusFilter('pendiente')} className="w-4 h-4 text-yellow-600 border-gray-300 rounded" />
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="w-3 h-3" /> Pendiente
                        </span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={onlyDiscrepant} onChange={() => setOnlyDiscrepant(prev => !prev)} className="w-4 h-4 text-orange-600 border-gray-300 rounded" />
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <AlertTriangle className="w-3 h-3" /> Solo desfasadas
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Materia</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horario</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profesor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aula</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asistencia</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {kardexData.kardex
                          .filter(entry => selectedCourseId ? entry.courseId === selectedCourseId : true)
                          .filter(entry => onlyDiscrepant ? entry.isDiscrepant : true)
                          .filter(entry => {
                            const showPlaneada = statusFilters.has('planeada');
                            const showEjecutada = statusFilters.has('ejecutada') && entry.executed;
                            const showPendiente = statusFilters.has('pendiente') && !entry.executed;
                            return showPlaneada || showEjecutada || showPendiente;
                          })
                          .map((entry, index) => (
                          <>
                            {statusFilters.has('planeada') && (
                              <tr key={`${entry.id}-${index}-planned`} className="bg-blue-50 border-l-4 border-l-blue-400">
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Calendar className="w-3 h-3" /> Planeada
                                  </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatDate(entry.classDate)}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {entry.sessionNumber && entry.totalSessions && (
                                    <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold rounded px-1.5 py-0.5 mr-1.5">
                                      Clase {entry.sessionNumber} de {entry.totalSessions}
                                    </span>
                                  )}
                                  {entry.courseCode} - {entry.courseName}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{entry.startTime} - {entry.endTime}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{entry.professorName || '-'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{entry.classroomName || '-'}</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-400">-</td>
                              </tr>
                            )}
                            {((entry.executed && statusFilters.has('ejecutada')) || (!entry.executed && statusFilters.has('pendiente'))) && (
                              <tr key={`${entry.id}-${index}-executed`} className={entry.executed ? 'bg-green-50 border-l-4 border-l-green-400' : 'bg-gray-50 border-l-4 border-l-gray-300'}>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {entry.executed ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <CheckCircle className="w-3 h-3" /> Ejecutada
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      <Clock className="w-3 h-3" /> Pendiente
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {entry.executed && entry.executedData ? formatDate(entry.executedData.classDate) : '-'}
                                  {entry.isDiscrepant && (
                                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      <AlertTriangle className="w-3 h-3" />
                                      {entry.discrepancyDays > 0 ? `+${entry.discrepancyDays}` : entry.discrepancyDays} días
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">-</td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {entry.executed && entry.executedData ? `${entry.executedData.startTime} - ${entry.executedData.endTime}` : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {entry.executed && entry.executedData ? (entry.executedData.professorName || '-') : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {entry.executed && entry.executedData ? (entry.executedData.classroomName || '-') : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm">
                                  {entry.executed ? (
                                    <span className="inline-flex items-center gap-1 text-gray-700">
                                      <Users className="w-4 h-4" />
                                      {entry.attendanceCount}/{entry.totalStudents}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {!selectedPeriodId && !loading && (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Selecciona un período para ver el Kardex de clases</p>
              </div>
            )}

            {selectedPeriodId && kardexData && kardexData.kardex.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
                <p>No hay clases programadas para este período</p>
              </div>
            )}
          </>
        )}

        {/* ─── TAB: SIN ASIGNACIÓN ─── */}
        {activeTab === 'unassigned' && (
          <>
            {loadingUnassigned && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-2 text-gray-500">Cargando materias sin asignación...</p>
              </div>
            )}

            {unassignedData && !loadingUnassigned && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      <span className="text-sm text-gray-600">Total con faltante</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{unassignedData.summary.total}</p>
                    <p className="text-xs text-gray-500 mt-1">sesiones sin completar</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-1">
                      <UserX className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-gray-600">Sin Profesor</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{unassignedData.summary.noProfessor}</p>
                    <p className="text-xs text-gray-500 mt-1">solo falta profesor</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                    <div className="flex items-center gap-2 mb-1">
                      <DoorOpen className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm text-gray-600">Sin Aula</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-600">{unassignedData.summary.noClassroom}</p>
                    <p className="text-xs text-gray-500 mt-1">solo falta aula</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-5 h-5 text-gray-500" />
                      <span className="text-sm text-gray-600">Sin Ambos</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-700">{unassignedData.summary.noBoth}</p>
                    <p className="text-xs text-gray-500 mt-1">sin profesor y sin aula</p>
                  </div>
                </div>

                {unassignedData.summary.total === 0 ? (
                  <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="text-lg font-medium text-green-800">¡Todas las sesiones tienen profesor y aula asignados!</p>
                    <p className="text-sm text-green-600 mt-1">No hay materias con asignaciones pendientes en este período.</p>
                  </div>
                ) : (
                  <>
                    {/* Filters row */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">Filtrar:</span>
                      </div>

                      {/* Type filter pills */}
                      {([
                        { key: 'all', label: 'Todos', color: 'bg-gray-100 text-gray-700 border-gray-300' },
                        { key: 'noProfessor', label: 'Sin Profesor', color: 'bg-red-100 text-red-700 border-red-300' },
                        { key: 'noClassroom', label: 'Sin Aula', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                        { key: 'noBoth', label: 'Sin Ambos', color: 'bg-gray-200 text-gray-800 border-gray-400' },
                      ] as { key: UnassignedFilter; label: string; color: string }[]).map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => setUnassignedFilter(opt.key)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${opt.color} ${
                            unassignedFilter === opt.key ? 'ring-2 ring-offset-1 ring-blue-400 font-bold' : 'opacity-70 hover:opacity-100'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}

                      {/* Course filter */}
                      {unassignedCourses.length > 1 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <select
                            value={unassignedCourseId || ''}
                            onChange={e => setUnassignedCourseId(Number(e.target.value) || null)}
                            className="text-sm border border-gray-300 rounded-md px-2 py-1"
                          >
                            <option value="">Todas las materias</option>
                            {unassignedCourses.map(c => (
                              <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                            ))}
                          </select>
                        </>
                      )}

                      {(unassignedFilter !== 'all' || unassignedCourseId) && (
                        <button
                          onClick={() => { setUnassignedFilter('all'); setUnassignedCourseId(null); }}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Falta</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Materia</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horario</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profesor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aula</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {filteredUnassigned.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                                No hay sesiones con este filtro
                              </td>
                            </tr>
                          ) : (
                            filteredUnassigned.map((item) => (
                              <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="flex flex-col gap-1">
                                    {item.missingProfessor && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                        <UserX className="w-3 h-3" /> Sin Profesor
                                      </span>
                                    )}
                                    {item.missingClassroom && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                        <DoorOpen className="w-3 h-3" /> Sin Aula
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="font-medium text-gray-900">{item.courseCode} - {item.courseName}</div>
                                  {item.sessionNumber && item.totalSessions && (
                                    <div className="text-xs text-gray-400">Sesión {item.sessionNumber} de {item.totalSessions}</div>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                                  {item.classDate ? formatDate(item.classDate) : <span className="text-gray-400">Sin fecha</span>}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                                  {item.startTime && item.endTime ? `${item.startTime} - ${item.endTime}` : '-'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {item.professorName ? (
                                    <span className="text-gray-700">{item.professorName}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-500 font-medium text-xs">
                                      <UserX className="w-3 h-3" /> No asignado
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {item.classroomName ? (
                                    <span className="text-gray-700">{item.classroomName}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-yellow-600 font-medium text-xs">
                                      <DoorOpen className="w-3 h-3" /> No asignada
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <p className="text-xs text-gray-400 mt-3">
                      Mostrando {filteredUnassigned.length} de {unassignedData.summary.total} sesiones con asignaciones pendientes
                    </p>
                  </>
                )}
              </>
            )}

            {!selectedPeriodId && !loadingUnassigned && (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Selecciona un período para ver las materias sin asignación</p>
              </div>
            )}
          </>
        )}

        {!selectedPeriodId && activeTab === 'kardex' && (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Selecciona un período para ver el Kardex de clases</p>
          </div>
        )}
      </div>
    </div>
  );
}
